import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  Signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TuiInputInline, TuiBadge } from '@taiga-ui/kit';
import { TranslatePipe } from '@ngx-translate/core';
import { IsLoadingService, XAlertService } from '@core/services';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestQcRatingsService } from '@core/services/request/request-qc-ratings.service';
import { RequestService } from '@core/services/request/request.service';
import {
  LogisticRequestRatingActionEnum,
  RequestFormQCRatingsTranslations,
  RequestStatuses,
} from '@core/types';
import type {
  LogisticRequestRatingList,
  RatingsStatus,
  RequestFormQCRatingsTranslationsPayload,
} from '@core/types';
import { RequestQualityControlStatus } from '@core/types/RequestQualityControl';
import {
  TuiDialogContext,
  TuiDialogService,
  TuiIcon,
  TuiButton,
  TuiHint,
  TuiCheckbox,
} from '@taiga-ui/core';
import { IconButtonComponent } from '@common/components';
import { TuiAutoFocus } from '@taiga-ui/cdk';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PolymorpheusContent } from '@taiga-ui/polymorpheus';
import {
  asyncScheduler,
  BehaviorSubject,
  EMPTY,
  of,
  Subject,
  switchMap,
} from 'rxjs';
import { FormsModule } from '@angular/forms';
import { catchError } from 'rxjs/operators';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { EntityTranslatePipe } from '@core/pipes';

@Component({
  selector: 'x-request-form-qc',
  imports: [
    CommonModule,
    TranslatePipe,
    TuiIcon,
    TuiBadge,
    TuiHint,
    IconButtonComponent,
    TuiButton,
    TuiCheckbox,
    FormsModule,
    TuiAutoFocus,
    TuiInputInline,
    EntityTranslatePipe,
  ],
  templateUrl: './request-form-qc.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormQcComponent implements OnInit {
  $isInsideSummary = input<boolean>(false, {
    alias: 'isInsideSummary',
  });

  private readonly requestService = inject(RequestService);
  private readonly requestQcRatingsService = inject(RequestQcRatingsService);
  private readonly dialogService$ = inject(TuiDialogService);
  private xAlertService = inject(XAlertService);
  public readonly permissionService = inject(OutkeepPermissionService);
  private outkeepTranslateService = inject(OutkeepTranslateService);
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  private readonly destroyRef = inject(DestroyRef);

  private translates: RequestFormQCRatingsTranslationsPayload = {
    [RequestFormQCRatingsTranslations.SuccessUpdatingRatingQCComment]:
      'Комментарий сохранен!',
    [RequestFormQCRatingsTranslations.ErrorUpdatingRating]:
      'Случилась ошибка при обновлении рейтинга',
    [RequestFormQCRatingsTranslations.ProblemSolved]: 'Проблема решена!',
    [RequestFormQCRatingsTranslations.Comment]: 'Комментарий',
    [RequestFormQCRatingsTranslations.NoComment]: 'Нет комментария!',
  };

  readonly $request = this.requestService.$request;
  public readonly $allQCRatings: Signal<LogisticRequestRatingList[]> =
    this.requestQcRatingsService.$entities;

  public ratingsStatus: RatingsStatus = {};

  public qualityControlComment = '';
  public qualityControlCommentEditing = false;

  public toggleRating$: Subject<LogisticRequestRatingList> =
    new Subject<LogisticRequestRatingList>();

  readonly RequestQualityControlStatusEnum = RequestQualityControlStatus;
  readonly $ratings: Signal<LogisticRequestRatingList[]> = computed(() => {
    return (
      this.$request()
        .ratingsWithDetails?.filter(rating => rating.type === 2)
        .sort((a, b) => b.level - a.level) // Sort `level` in descending order
        .sort((a, b) => (a.fixed === b.fixed ? 0 : a.fixed ? 1 : -1)) || [] // Sort `fixed` in ascending order
    );
  });

  readonly $qualityControlStatusText = computed(() => {
    const request = this.$request();

    if (request.statusId!.id !== RequestStatuses.Completed) {
      return 'request_form.qc.qc_status_not_completed'; // Заявка не завершена
    }
    switch (request.qualityControlStatus) {
      case RequestQualityControlStatus.NoErrors:
        return 'request_form.qc.qc_status_no_errors'; // Заявка без ошибок
      case RequestQualityControlStatus.HasErrors:
        return 'request_form.qc.qc_status_some_errors'; // Имеются ошибки
      case RequestQualityControlStatus.HasCriticalErrors:
        return 'request_form.qc.qc_status_critical_errors'; // Имеются критические ошибки
      case RequestQualityControlStatus.AllProblemsSolved:
        return 'request_form.qc.qc_status_all_solved'; // Все проблемы решены
      case RequestQualityControlStatus.ErrorsPartiallySolved:
        return 'request_form.qc.qc_status_partially_solved'; // Ошибки исправлены частично
      default:
        return 'request_form.qc.qc_status_not_checked'; // Заявка не проверена
    }
  });

  constructor() {
    effect(() => {
      if (this.$allQCRatings().length) {
        this.ratingsStatus = this.requestQcRatingsService.$ratingsStatus();
      }
    });
  }

  ngOnInit(): void {
    this.outkeepTranslateService
      .translate(Object.values(RequestFormQCRatingsTranslations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));

    this.toggleRating$
      .pipe(
        switchMap(rating =>
          this.requestQcRatingsService.toggleRating(
            rating.id,
            this.ratingsStatus[rating.id]
              ? LogisticRequestRatingActionEnum.ON
              : LogisticRequestRatingActionEnum.OFF,
          ),
        ),
        catchError(() => {
          this.xAlertService.error(
            this.translates[
              RequestFormQCRatingsTranslations.ErrorUpdatingRating
            ],
          );
          asyncScheduler.schedule(() => {
            this.requestService.reloadRequest();
          }, 1000);

          return EMPTY;
        }),
        switchMap(() => {
          asyncScheduler.schedule(() => {
            this.requestService.reloadRequest();
          }, 1000);

          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public showQualityStatusesModal(
    content: PolymorpheusContent<TuiDialogContext>,
  ) {
    this.dialogService$
      .open(content)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  @pure
  public getRatingHintText(rating: LogisticRequestRatingList): string {
    if (!rating) {
      return '';
    }
    if (rating.fixed) {
      return `${this.translates[RequestFormQCRatingsTranslations.ProblemSolved]}\n${rating.installerComment ? `${this.translates[RequestFormQCRatingsTranslations.Comment]}: ` + rating.installerComment : `${this.translates[RequestFormQCRatingsTranslations.NoComment]}`}!\n${rating.fixedBy}\n${rating.updatedAt}`;
    } else {
      return (
        `${rating.qualityControlComment ? `${this.translates[RequestFormQCRatingsTranslations.Comment]}: ` + rating.qualityControlComment : `${this.translates[RequestFormQCRatingsTranslations.NoComment]}`}` +
          `\n${rating.createdBy}\n${rating.createdAt}` || ''
      );
    }
  }

  public handleButtonClick(rating: LogisticRequestRatingList): void {
    this.ratingsStatus[rating.id] = !this.ratingsStatus[rating.id];
    this.toggleRating$.next(rating);
  }

  public openRatingDetailsModal(
    content: PolymorpheusContent<TuiDialogContext>,
    rating: LogisticRequestRatingList,
  ) {
    this.qualityControlComment = rating.qualityControlComment || '';

    this.dialogService$
      .open(content, { data: rating })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  toggleEditQCComment(): void {
    this.qualityControlCommentEditing = !this.qualityControlCommentEditing;
  }

  onFocusedQCCommentChange(
    focused: boolean,
    rating: LogisticRequestRatingList,
  ): void {
    if (!focused) {
      this.qualityControlCommentEditing = false;
      this.saveQcComment(rating);
    }
  }

  public saveQcComment(rating: LogisticRequestRatingList): void {
    if (this.qualityControlComment === rating.qualityControlComment) {
      return;
    }
    this.qualityControlCommentEditing = false;
    this.requestQcRatingsService
      .updateRatingQCComment$({
        ...rating,
        qualityControlComment: this.qualityControlComment,
      })
      .pipe(
        catchError(() => {
          this.xAlertService.error(
            this.translates[
              RequestFormQCRatingsTranslations.ErrorUpdatingRating
            ],
          );
          this.requestService.reloadRequest();

          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.requestService.reloadRequest();
        this.xAlertService.success(
          this.translates[
            RequestFormQCRatingsTranslations.SuccessUpdatingRatingQCComment
          ],
        );
      });
  }
}
